import { useEffect, useState } from "react";
import { approveExpense, createExpense, listExpenses, rejectExpense } from "../api/finance.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface Expense {
  id: string;
  category: string;
  vendor: string | null;
  description: string;
  amount: number;
  taxAmount: number;
  status: string;
  expenseDate: string;
  version: number;
}

const STATUS_COLOR: Record<string, string> = {
  PENDING: "text-yellow-400 bg-yellow-900/30",
  APPROVED: "text-green-400 bg-green-900/30",
  REJECTED: "text-red-400 bg-red-900/30",
};

const CATEGORIES = [
  "Software",
  "Hardware",
  "Marketing",
  "Travel",
  "Office",
  "Contractor",
  "Cloud",
  "Legal",
  "Other",
];

function currency(n: number) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
}

export function FinanceExpensesPage() {
  const { token } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    category: "Software",
    description: "",
    amount: "",
    vendor: "",
    taxAmount: "0",
    expenseDate: new Date().toISOString().slice(0, 10),
  });
  const [creating, setCreating] = useState(false);

  const reload = () => {
    void listExpenses(token).then((d) => {
      setExpenses((d as { items: Expense[] }).items ?? []);
      setLoading(false);
    });
  };

  useEffect(() => {
    reload();
  }, []);

  const create = async () => {
    if (!form.description || !form.amount) return;
    setCreating(true);
    await createExpense(token, {
      category: form.category,
      description: form.description,
      amount: parseFloat(form.amount),
      vendor: form.vendor || undefined,
      taxAmount: parseFloat(form.taxAmount) || 0,
      expenseDate: form.expenseDate,
    });
    setShowForm(false);
    setCreating(false);
    reload();
  };

  const approve = async (exp: Expense) => {
    if (!window.confirm(`Approve expense: ${exp.description}?`)) return;
    await approveExpense(token, exp.id, exp.version);
    reload();
  };

  const reject = async (exp: Expense) => {
    await rejectExpense(token, exp.id, exp.version);
    reload();
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Expenses</h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="rounded bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-500"
          >
            {showForm ? "Cancel" : "Record Expense"}
          </button>
        </div>
        {showForm && (
          <div className="mb-6 space-y-3 rounded-lg border border-gray-700 bg-gray-900 p-4">
            <div className="grid grid-cols-2 gap-3">
              <select
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-300"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <input
                value={form.vendor}
                onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))}
                placeholder="Vendor (optional)"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Description"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder="Amount (₹)"
                type="number"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.taxAmount}
                onChange={(e) => setForm((f) => ({ ...f, taxAmount: e.target.value }))}
                placeholder="Tax Amount (₹)"
                type="number"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.expenseDate}
                onChange={(e) => setForm((f) => ({ ...f, expenseDate: e.target.value }))}
                type="date"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white"
              />
            </div>
            <button
              onClick={() => void create()}
              disabled={creating}
              className="rounded bg-green-700 px-4 py-2 text-sm text-white hover:bg-green-600 disabled:opacity-50"
            >
              {creating ? "Saving..." : "Save Expense"}
            </button>
          </div>
        )}
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-2">
            {expenses.length === 0 && <p className="text-gray-400">No expenses recorded.</p>}
            {expenses.map((exp) => (
              <div
                key={exp.id}
                className="flex items-center gap-3 rounded-lg border border-gray-800 bg-gray-900 p-3"
              >
                <span className="shrink-0 rounded bg-gray-800 px-2 py-0.5 text-xs text-gray-400">
                  {exp.category}
                </span>
                <span className="flex-1 text-sm text-white">{exp.description}</span>
                {exp.vendor && <span className="text-xs text-gray-500">{exp.vendor}</span>}
                <span className="text-sm font-semibold text-white">{currency(exp.amount)}</span>
                <span
                  className={`shrink-0 rounded px-2 py-0.5 text-xs ${STATUS_COLOR[exp.status] ?? "bg-gray-800 text-gray-400"}`}
                >
                  {exp.status}
                </span>
                {exp.status === "PENDING" && (
                  <>
                    <button
                      onClick={() => void approve(exp)}
                      className="rounded bg-green-900/50 px-2 py-0.5 text-xs text-green-300 hover:bg-green-900"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => void reject(exp)}
                      className="rounded bg-red-900/50 px-2 py-0.5 text-xs text-red-300 hover:bg-red-900"
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
